import React from "react";
import ReactMarkdown from "react-markdown";
import { cn } from "../lib/cn";

type Tone =
  | "novidade"
  | "melhoria"
  | "correcao"
  | "seguranca"
  | "interface"
  | "performance"
  | "neutro";

interface SectionCategory {
  tone: Tone;
  match: RegExp;
  swatchClass: string;
}

const categories: SectionCategory[] = [
  {
    tone: "novidade",
    match: /\b(novidad|recurso|feature|adicionad|introduc)/i,
    swatchClass: "bg-emerald-600 dark:bg-emerald-400",
  },
  {
    tone: "melhoria",
    match: /\b(melhori|aprimora|otimiza|refator|atualiz|aprimorament)/i,
    swatchClass: "bg-primary",
  },
  {
    tone: "correcao",
    match: /\b(corre[cç][aã]o|corrig|fix|bug|reparo|resolvid)/i,
    swatchClass: "bg-amber-600 dark:bg-amber-400",
  },
  {
    tone: "seguranca",
    match: /\b(seguran[cç]a|security|vulnerabilid|cve|patch)/i,
    swatchClass: "bg-rose-600 dark:bg-rose-400",
  },
  {
    tone: "interface",
    match: /\b(interface|design|ui|ux|visual|aparenc|tema)/i,
    swatchClass: "bg-violet-600 dark:bg-violet-400",
  },
  {
    tone: "performance",
    match: /\b(performance|desempenho|velocidad|rapidez|otimiza[cç][aã]o)/i,
    swatchClass: "bg-sky-600 dark:bg-sky-400",
  },
];

const stripLeadingEmojiAndPunct = (text: string): string =>
  text
    .replace(/^(?:\p{Extended_Pictographic}|\p{So}|️|‍)+/gu, "")
    .replace(/^[\s:\u00b7\u2022\u2014\u2013-]+/u, "")
    .trim();

const detectCategory = (raw: string): SectionCategory | null => {
  const cleaned = stripLeadingEmojiAndPunct(raw).toLowerCase();
  return categories.find((c) => c.match.test(cleaned)) || null;
};

const childrenToRawText = (nodeChildren: React.ReactNode): string =>
  React.Children.toArray(nodeChildren)
    .map((c) => (typeof c === "string" ? c : ""))
    .join("");

interface ChangelogMarkdownProps {
  children: string;
  className?: string;
}

export const ChangelogMarkdown: React.FC<ChangelogMarkdownProps> = ({
  children,
  className,
}) => (
  <div className={cn("prose-changelog", className)}>
    <ReactMarkdown
      components={{
        h1: ({ children: nodeChildren, ...props }) => {
          const raw = childrenToRawText(nodeChildren);
          const cleaned = stripLeadingEmojiAndPunct(raw);
          return <h1 {...props}>{cleaned || raw}</h1>;
        },
        h2: ({ children: nodeChildren, ...props }) => {
          const raw = childrenToRawText(nodeChildren);
          const cleaned = stripLeadingEmojiAndPunct(raw);
          return <h2 {...props}>{cleaned || raw}</h2>;
        },
        h3: ({ children: nodeChildren, ...props }) => {
          const raw = childrenToRawText(nodeChildren);
          const cleaned = stripLeadingEmojiAndPunct(raw);
          const category = detectCategory(raw);
          return (
            <h3 {...props}>
              <span
                aria-hidden="true"
                className={cn(
                  "inline-block w-1.5 h-1.5 rounded-sm shrink-0",
                  category?.swatchClass ?? "bg-muted-foreground/40"
                )}
              />
              <span>{cleaned || raw}</span>
            </h3>
          );
        },
      }}
    >
      {children}
    </ReactMarkdown>
  </div>
);
